import React, { useEffect, useMemo, useState } from 'react';
import { TopNav } from '../components/TopNav';
import { listAgenda, moveAgendaDate, moveAgendaStatus, createAgenda, updateAgenda } from '@/features/agenda/services/agenda.api';
import type { AgendaItemDto, AgendaStatus } from '@/features/agenda/types';
import { useToast } from '@/components/ToastProvider';
import AgendaDetailDialog from '@/components/AgendaDetailDialog';

interface PageProps { theme: string; toggleTheme: () => void }

const statusCols: { key: AgendaStatus; label: string }[] = [
  { key: 'PROSPECCAO', label: 'Prospecção' },
  { key: 'HOLD', label: 'Hold' },
  { key: 'NEGOCIACAO', label: 'Negociação' },
  { key: 'CONFIRMADO', label: 'Confirmado' },
  { key: 'ENTREGUE', label: 'Entregue' },
];

const AgendaPage: React.FC<PageProps> = ({ theme, toggleTheme }) => {
  const toast = useToast();
  const [mode, setMode] = useState<'calendar'|'pipeline'|'map'>('calendar');
  const [items, setItems] = useState<AgendaItemDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [type, setType] = useState<string>('');
  const [status, setStatus] = useState<string>('');
  const [hasLocation, setHasLocation] = useState<boolean|undefined>(undefined);
  const [selected, setSelected] = useState<AgendaItemDto | null>(null);

  const reload = async () => {
    setLoading(true);
    try {
      const data = await listAgenda({ type: (type || undefined) as any, status: (status as any) || undefined, hasLocation });
      setItems(data.items);
    } finally { setLoading(false); }
  };

  useEffect(() => { reload(); }, [type, status, hasLocation]);

  const byDate = useMemo(() => {
    const g: Record<string, AgendaItemDto[]> = {};
    for (const it of items) {
      const d = it.startAt ? new Date(it.startAt) : null;
      const key = d ? d.toISOString().slice(0,10) : 'Sem data';
      (g[key] ||= []).push(it);
    }
    return Object.entries(g).sort(([a],[b]) => a.localeCompare(b));
  }, [items]);

  const handleDropDate = async (it: AgendaItemDto, day: string) => {
    try {
      await moveAgendaDate(it.id, new Date(day).toISOString());
      toast.success('Data atualizada');
      await reload();
    } catch (e:any) { toast.error('Falha ao atualizar data', e?.message); }
  };

  const handleDropStatus = async (it: AgendaItemDto, st: AgendaStatus) => {
    try { await moveAgendaStatus(it.id, st); toast.success('Status atualizado'); await reload(); }
    catch (e:any) { toast.error('Falha ao alterar status', e?.message); }
  };

  const CalendarView = () => (
    <div className="space-y-3">
      {byDate.map(([day, list]) => (
        <div key={day} className="bg-surface border border-default rounded-lg p-3">
          <div className="text-sm font-semibold mb-2">{day}</div>
          <div className="flex flex-wrap gap-2 min-h-[48px]"
               onDragOver={(e)=>e.preventDefault()}
               onDrop={(e)=>{
                const id = e.dataTransfer.getData('text/plain');
                const it = items.find(x=>x.id===id); if(it) handleDropDate(it, day);
               }}>
            {list.map(it => (
              <div key={it.id}
                   draggable
                   onDragStart={(e)=> e.dataTransfer.setData('text/plain', it.id)}
                   className={`px-3 py-2 rounded-md border border-default bg-subtle text-xs cursor-grab`}
                   onClick={()=> setSelected(it)}
                   title={`${it.type}${it.locationName? ' – '+it.locationName:''}`}
              >
                <div className="font-semibold">{it.type}{it.locationName? ' – '+it.locationName:''}</div>
                <div className="flex items-center gap-2 text-muted">
                  <span>{it.status}</span>
                  {it.riskFlag && <span title="Risco">⚠️</span>}
                  {!!it.marginCents && <span title="Margem">💸</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  const PipelineView = () => (
    <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
      {statusCols.map(col => (
        <div key={col.key} className="bg-surface border border-default rounded-lg p-2"
             onDragOver={(e)=>e.preventDefault()}
             onDrop={(e)=>{ const id=e.dataTransfer.getData('text/plain'); const it=items.find(x=>x.id===id); if(it) handleDropStatus(it, col.key); }}>
          <div className="text-sm font-bold mb-2">{col.label}</div>
          <div className="space-y-2 min-h-[60px]">
            {items.filter(i=>i.status===col.key).map(it => (
              <div key={it.id}
                   draggable
                   onDragStart={(e)=> e.dataTransfer.setData('text/plain', it.id)}
                   className="px-3 py-2 rounded-md border border-default bg-subtle text-xs cursor-grab"
                   onClick={()=> setSelected(it)}>
                <div className="font-semibold truncate">{it.title || `${it.type}${it.locationName? ' – '+it.locationName:''}`}</div>
                <div className="flex items-center justify-between text-muted">
                  <span>{it.startAt?.slice(0,10) || '-'}</span>
                  {it.marginCents ? <span>💸</span> : <span />}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  const MapView = () => {
    const groups: { id: string; label: string; lat: number; lng: number; count: number }[] = [];
    const push = (id:string,label:string,lat?:number,lng?:number)=>{ if(lat==null||lng==null) return; const ex=groups.find(g=>g.id===id); if(ex){ ex.count++; } else { groups.push({ id, label, lat, lng, count:1 }); } };
    items.forEach(it=>{
      if (it.parentId) push(it.parentId, `Turnê (${it.parentId.slice(0,4)}…)`, it.lat, it.lng); else push(it.id, it.title || it.type, it.lat, it.lng);
    });
    return (
      <div className="relative w-full h-96 rounded-lg overflow-hidden border border-default">
        {/* Leaflet real pode ser integrado aqui; por simplicidade, um mapa estático placeholder com pontos absolutos não faz sentido; então deixo mensagem caso não haja geodados */}
        {groups.length===0 ? (
          <div className="w-full h-full grid place-items-center text-sm text-muted bg-subtle">Sem compromissos geolocalizados</div>
        ) : (
          <div className="w-full h-full grid place-items-center text-sm text-muted bg-subtle">Mapa (Leaflet) pronto para integração — pontos: {groups.length}</div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-body">
      <TopNav theme={theme} toggleTheme={toggleTheme} />
      <main className="max-w-7xl mx-auto p-3 md:p-4 space-y-4">
        <header className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-extrabold text-primary">Agenda</h1>
            <p className="text-sm text-muted">Compromissos estratégicos com três visões.</p>
          </div>
          <div className="flex items-center gap-2">
            <select value={type} onChange={e=>setType(e.target.value)} className="px-3 py-2 rounded-md border border-default bg-transparent text-sm">
              <option value="">Tipo</option>
              <option>SHOW</option><option>TOUR</option><option>COLLAB</option><option>PROMO</option><option>OPERATIONS</option><option>NETWORKING</option>
            </select>
            <select value={status} onChange={e=>setStatus(e.target.value)} className="px-3 py-2 rounded-md border border-default bg-transparent text-sm">
              <option value="">Status</option>
              {statusCols.map(s=> <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
            <select value={hasLocation===undefined ? '' : hasLocation? '1':'0'} onChange={e=> setHasLocation(e.target.value===''? undefined : e.target.value==='1')} className="px-3 py-2 rounded-md border border-default bg-transparent text-sm">
              <option value="">Localização</option>
              <option value="1">Com localização</option>
              <option value="0">Sem localização</option>
            </select>
          </div>
        </header>

        <div className="flex items-center gap-2 bg-zinc-200 dark:bg-zinc-800 p-1 rounded-full w-fit">
          {['calendar','pipeline','map'].map(m => (
            <button key={m} onClick={()=>setMode(m as any)} className={`px-4 py-1.5 text-sm font-semibold rounded-full ${mode===m? 'bg-green-500 text-black':'text-zinc-600 dark:text-zinc-300 hover:bg-zinc-300 dark:hover:bg-zinc-700'}`}>{m==='calendar'? 'Calendário': m==='pipeline'? 'Pipeline':'Mapa'}</button>
          ))}
        </div>

        {loading ? (
          <div className="text-sm text-muted">Carregando...</div>
        ) : mode==='calendar' ? <CalendarView/> : mode==='pipeline' ? <PipelineView/> : <MapView/>}
        {selected && (
          <AgendaDetailDialog open={!!selected} onClose={()=>setSelected(null)} item={selected} onSaved={async()=>{ await reload(); }} />
        )}
      </main>
    </div>
  );
};

export default AgendaPage;
